// filepath: /src/sections/HeroSection.js
import React, { useEffect, useState, useRef } from 'react';
import './HeroSection.css';
// Importar assets para la animación de cristal
import cristalFijo from '../assets/imagenes/cristal-roto-fijo.png';
import cristalVolando from '../assets/imagenes/cristal-volando.png';
import cristalSonido from '../assets/song/cristal.mp3';

// Componente de contador animado con duración extendida y movimiento realista
const AnimatedCounter = ({ end, duration = 180000, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const countRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const startValue = 0;
    let lastUpdateTime = 0;
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Usar función easeOutCubic para un incremento más natural
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      // Añadir pequeñas variaciones para hacer el incremento más realista
      const noise = Math.sin(currentTime * 0.001) * 0.02; // Pequeña variación sinusoidal
      const adjustedProgress = Math.max(0, Math.min(1, easeProgress + noise));
      
      const targetCount = Math.floor(adjustedProgress * (end - startValue) + startValue);
      
      // Actualizar solo si ha pasado suficiente tiempo (crear efecto de pasos)
      if (currentTime - lastUpdateTime > 100 || progress >= 1) { // Actualizar cada 100ms
        setCount(targetCount);
        lastUpdateTime = currentTime;
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, isVisible]);

  return (
    <div ref={countRef} className="stat-number">
      {prefix}{count}{suffix}
    </div>
  );
};

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  // Estados para la animación de cristal roto
  const [isBreaking, setIsBreaking] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [animationStep, setAnimationStep] = useState(0); // 0: normal, 1: zoom, 2: cracks, 3: message
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const audioRef = useRef(null);

  const fullMessage = "Rompimos lo tradicional y Recreamos el Futuro de la Consulta Médica.";

  useEffect(() => {
    setIsVisible(true);
    
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleCTAClick = () => {
    const element = document.querySelector('#acceso');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLearnMoreClick = () => {
    const element = document.querySelector('#nosotros');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Función para manejar la animación de cristal roto
  const handleGlassBreak = () => {
    if (isBreaking) return; // Prevenir múltiples clics durante la animación
    
    setIsBreaking(true);
    setAnimationStep(2); // Ir directo a las grietas, sin zoom
    
    // Inmediatamente mostrar grietas y reproducir sonido
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        generateGlassBreakSound();
      });
    } else {
      generateGlassBreakSound();
    }
    
    // Después de 0.7s, empezar a desvanecer las grietas
    setTimeout(() => {
      setAnimationStep(3);
      
      // Después de 1s más, mostrar el mensaje
      setTimeout(() => {
        setShowMessage(true);
        startTypingEffect();
        setIsBreaking(false);
        setAnimationStep(0); // Reset para permitir nueva animación
      }, 1000);
    }, 700);
  };

  // Efecto de escritura letra por letra
  const startTypingEffect = () => {
    setIsTyping(true);
    setTypedText('');
    
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex < fullMessage.length) {
        setTypedText(fullMessage.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
        
        // Cerrar automáticamente el mensaje después de 1.5s
        setTimeout(() => {
          closeMessage();
        }, 1500);
      }
    }, 80); // Velocidad de escritura: 80ms por letra
  };

  // Función para cerrar el mensaje y resetear el efecto
  const closeMessage = () => {
    setShowMessage(false);
    setTypedText('');
    setIsTyping(false);
  };

  const generateGlassBreakSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Crear un ruido blanco para simular el sonido del cristal
      const bufferSize = audioContext.sampleRate * 0.5; // 0.5 segundos
      const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
      const output = buffer.getChannelData(0);
      
      // Generar ruido con envolvente
      for (let i = 0; i < bufferSize; i++) {
        const envelope = Math.exp(-i / (bufferSize * 0.3)); // Decaimiento exponencial
        output[i] = (Math.random() * 2 - 1) * envelope * 0.3;
      }
      
      const whiteNoise = audioContext.createBufferSource();
      whiteNoise.buffer = buffer;
      
      // Filtro paso alto para sonido más cristalino
      const highpass = audioContext.createBiquadFilter();
      highpass.type = 'highpass';
      highpass.frequency.value = 2000;
      
      // Conectar y reproducir
      whiteNoise.connect(highpass);
      highpass.connect(audioContext.destination);
      whiteNoise.start(0);
    } catch (error) {
      console.log('No se pudo generar sonido:', error);
    }
  };

  return (
    <section id="hero" className={`hero-section ${isVisible ? 'visible' : ''}`}>
      {/* Animated Background */}
      <div className="hero-background">
        <div 
          className="parallax-orb orb-1"
          style={{
            transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`
          }}
        ></div>
        <div 
          className="parallax-orb orb-2"
          style={{
            transform: `translate(${mousePosition.x * -0.01}px, ${mousePosition.y * -0.01}px)`
          }}
        ></div>
        <div 
          className="parallax-orb orb-3"
          style={{
            transform: `translate(${mousePosition.x * 0.015}px, ${mousePosition.y * 0.015}px)`
          }}
        ></div>
      </div>

      <div className="container">
        <div className="hero-content">
          {/* Main Content */}
          <div className="hero-text">
            <div className="hero-badge animate-slide-up">
              <span className="badge-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1l2.5 5.5L16 8l-5.5 1.5L8 15l-2.5-5.5L0 8l5.5-1.5L8 1z" fill="currentColor"/>
                </svg>
              </span>
              Innovación en Salud Digital
            </div>

            <h1 className="hero-title animate-slide-up">
              Avanzamos el Futuro
              <span className="gradient-text"> Cuidando con la IA</span>
              <br />
              Pensando en Ti
            </h1>

            <p className="hero-subtitle animate-slide-up">
              Transformamos la atención médica con tecnología de vanguardia, 
              inteligencia artificial y una experiencia de usuario excepcional 
              que conecta pacientes y profesionales de la salud.
            </p>

            <div className="hero-stats animate-slide-up">
              <div className="stat-item glass-card">
                <AnimatedCounter end={10} duration={180000} suffix="K+" />
                <div className="stat-label">Pacientes Activos</div>
              </div>
              <div className="stat-item glass-card">
                <AnimatedCounter end={500} duration={195000} suffix="+" />
                <div className="stat-label">Profesionales</div>
              </div>
              <div className="stat-item glass-card">
                <AnimatedCounter end={99} duration={210000} suffix="%" />
                <div className="stat-label">Satisfacción</div>
              </div>
            </div>

            <div className="hero-actions animate-slide-up">
              <button 
                className="glass-button primary large"
                onClick={handleCTAClick}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 2v16M2 10h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Comenzar Ahora
              </button>
              
              <button 
                className="glass-button secondary large"
                onClick={handleLearnMoreClick}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 18l-6-6 1.5-1.5L10 15l4.5-4.5L16 12l-6 6z" fill="currentColor"/>
                </svg>
                Conocer Más
              </button>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="hero-visual animate-slide-right">
            <div 
              className="glass-dashboard"
              onClick={handleGlassBreak}
              style={{ cursor: isBreaking ? 'default' : 'pointer' }}
            >
              {/* Overlays de cristal roto - NO afectan el diseño existente */}
              <div 
                className={`crystal-overlay crystal-fixed ${animationStep >= 2 ? 'show' : ''}`}
                style={{ backgroundImage: `url(${cristalFijo})` }}
              ></div>
              <div 
                className={`crystal-overlay crystal-flying ${
                  animationStep === 2 ? 'show' : animationStep >= 3 ? 'fade-out' : ''
                }`}
                style={{ backgroundImage: `url(${cristalVolando})` }}
              ></div>
              
              {/* Mensaje animado después de romper el cristal - DENTRO del dashboard */}
              {showMessage && (
                <div className="dashboard-break-message animate-message">
                  <h2 className={`message-text ${isTyping ? 'typing-text' : ''}`}>
                    {typedText}
                  </h2>
                  <button 
                    className="message-close glass-button secondary"
                    onClick={closeMessage}
                  >
                    ✕
                  </button>
                </div>
              )}
              
              <div className="dashboard-header">
                <div className="dashboard-title">Think Health Dashboard</div>
                <div className="dashboard-controls">
                  <div className="control-dot red"></div>
                  <div className="control-dot yellow"></div>
                  <div className="control-dot green"></div>
                </div>
              </div>
              
              <div className="dashboard-content">
                <div className="health-metric">
                  <div className="metric-label">Ritmo Cardíaco</div>
                  <div className="metric-value">72 <span>bpm</span></div>
                  <div className="metric-chart">
                    <svg width="120" height="40" viewBox="0 0 120 40">
                      <path 
                        d="M0,20 Q10,10 20,20 T40,20 Q50,15 60,20 T80,20 Q90,25 100,20 T120,20" 
                        stroke="#60a5fa" 
                        strokeWidth="2" 
                        fill="none"
                        className="heartbeat-line"
                      />
                    </svg>
                  </div>
                </div>

                <div className="health-metric">
                  <div className="metric-label">Presión Arterial</div>
                  <div className="metric-value">120/80 <span>mmHg</span></div>
                  <div className="status-indicator optimal">Óptima</div>
                </div>

                <div className="health-metric">
                  <div className="metric-label">Próxima Cita</div>
                  <div className="metric-value">15 Jul <span>10:00 AM</span></div>
                  <div className="status-indicator pending">Pendiente</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        {/* <div className="scroll-indicator animate-float">
          <div className="scroll-text">Desliza para explorar</div>
          <div className="scroll-arrow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div> */}
      </div>

      {/* Audio para el sonido de cristal roto */}
      <audio 
        ref={audioRef} 
        preload="auto"
        src={cristalSonido}
      />
    </section>
  );
};

export default HeroSection;
